/**
 * OrderDraftContext — in-memory store for prepared order drafts.
 *
 * Keyed by jobId (one active draft per job). Lifecycle:
 *   • prepareDraft(job, vendorGroups, preparedBy, notes?) → creates "prepared" draft
 *   • voidDraft(jobId, reason)                            → marks draft "voided"
 *   • markPushed(jobId, poIds)                            → marks draft "pushed"
 *   • subscribeToChangeOrders                             → auto-voids drafts when
 *                                                           a new Signed CO appears
 *
 * Push validation + integrity logging is handled by callers (POOrderingModal,
 * Job Detail, Ordering Queue) so they can route forensic events to the
 * right surfaces (Job Timeline + FrictionLog).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAppData } from "./AppDataContext";
import { useFrictionLog } from "./FrictionLogContext";
import { useRole } from "./RoleContext";
import {
  buildPreparedDraft,
  getLastSignedChangeOrderAt,
  summarizeDraft,
  type OrderDraft,
  type OrderDraftVendorGroup,
  type OrderDraftVoidReason,
} from "./orderDraft";
import type { ActivityEntry, Job } from "./types";

// localStorage persistence — same survival semantics as other in-memory state
// (AppDataContext is itself memory-only). Drafts here outlive page reloads so
// the prepped snapshot remains the authoritative input for Push.
const LS_KEY = "koredor.orderDrafts.v1";
function loadDrafts(): Record<string, OrderDraft> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, OrderDraft>) : {};
  } catch {
    return {};
  }
}
function saveDrafts(drafts: Record<string, OrderDraft>) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(drafts)); } catch { /* quota / private mode — non-fatal */ }
}

interface PrepareInput {
  jobId: string;
  vendorGroups: OrderDraftVendorGroup[];
  preparedBy: string;
  notes?: string;
}

interface OrderDraftContextValue {
  drafts: Record<string, OrderDraft>;                 // jobId → draft
  getDraft: (jobId: string) => OrderDraft | undefined;
  prepareDraft: (input: PrepareInput) => OrderDraft;
  voidDraft: (jobId: string, reason: OrderDraftVoidReason, changeOrderId?: string) => void;
  markPushed: (jobId: string, poIds: string[], pushedBy: string) => void;
  /**
   * Log an integrity violation. The Push UI calls this when BOM hash drift is
   * detected without a Signed CO. Writes a high-severity FrictionLog entry
   * tagged "order_integrity_violation" so engineering can trace the source.
   */
  logIntegrityViolation: (input: {
    jobId: string;
    expectedHash: string;
    actualHash: string;
    description: string;
  }) => void;
}

const OrderDraftContext = createContext<OrderDraftContextValue | null>(null);

export function OrderDraftProvider({ children }: { children: ReactNode }) {
  const [drafts, setDrafts] = useState<Record<string, OrderDraft>>(() => loadDrafts());
  const { jobs, setJobs } = useAppData();
  const { addEntry } = useFrictionLog();
  const { role, roleInfo } = useRole();

  // Persist on every change so refreshes don't lose drafts.
  useEffect(() => { saveDrafts(drafts); }, [drafts]);

  // Track last-seen signed CO timestamps per job so we can detect new ones
  // after a draft was prepared and auto-void with reason "change_order".
  const lastSignedCoRef = useRef<Record<string, string | undefined>>({});

  // Helper — append an ordering-tagged ActivityEntry to a job's timeline.
  const appendActivity = useCallback((jobId: string, action: string, user: string) => {
    const entry: ActivityEntry = {
      id: `act-orderdraft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      date: new Date().toISOString(),
      action,
      user,
      noteType: "ordering",
    };
    setJobs((prev: Job[]) => prev.map((j) =>
      j.id === jobId
        ? { ...j, activityHistory: [...(j.activityHistory || []), entry] }
        : j,
    ));
  }, [setJobs]);

  const getDraft = useCallback((jobId: string) => drafts[jobId], [drafts]);

  const prepareDraft = useCallback((input: PrepareInput): OrderDraft => {
    const job = jobs.find((j) => j.id === input.jobId);
    if (!job) throw new Error(`Job ${input.jobId} not found`);
    const draft = buildPreparedDraft({
      job,
      vendorGroups: input.vendorGroups,
      preparedBy: input.preparedBy,
      notes: input.notes,
    });
    setDrafts((prev) => ({ ...prev, [input.jobId]: draft }));
    const sum = summarizeDraft(draft);
    appendActivity(
      input.jobId,
      `Order prepared — ${sum.vendorCount} vendor${sum.vendorCount === 1 ? "" : "s"}, ${sum.itemCount} item${sum.itemCount === 1 ? "" : "s"} (awaiting Push)`,
      input.preparedBy,
    );
    return draft;
  }, [jobs, appendActivity]);

  const voidDraft = useCallback((jobId: string, reason: OrderDraftVoidReason, changeOrderId?: string) => {
    setDrafts((prev) => {
      const existing = prev[jobId];
      if (!existing || existing.status !== "prepared") return prev;
      return {
        ...prev,
        [jobId]: {
          ...existing,
          status: "voided",
          voidedAt: new Date().toISOString(),
          voidedReason: reason,
          voidedChangeOrderId: changeOrderId,
        },
      };
    });
    const reasonLabel =
      reason === "change_order"        ? `Change Order${changeOrderId ? ` ${changeOrderId}` : ""}` :
      reason === "integrity_violation" ? "BOM integrity violation" :
      reason === "manual"              ? "manual void" :
      "system";
    appendActivity(jobId, `Order draft voided — ${reasonLabel}. Re-prep required.`, roleInfo?.label || role);
  }, [appendActivity, role, roleInfo]);

  const markPushed = useCallback((jobId: string, poIds: string[], pushedBy: string) => {
    setDrafts((prev) => {
      const existing = prev[jobId];
      if (!existing) return prev;
      return {
        ...prev,
        [jobId]: {
          ...existing,
          status: "pushed",
          pushedAt: new Date().toISOString(),
          pushedBy,
          pushedPoIds: poIds,
        },
      };
    });
    appendActivity(
      jobId,
      `Order pushed to vendor${poIds.length === 1 ? "" : "s"} — ${poIds.length} PO${poIds.length === 1 ? "" : "s"} created`,
      pushedBy,
    );
  }, [appendActivity]);

  const logIntegrityViolation = useCallback((input: {
    jobId: string;
    expectedHash: string;
    actualHash: string;
    description: string;
  }) => {
    addEntry({
      id: `frict-integ-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      userId: role,
      userRole: roleInfo?.label || role,
      moduleName: "Ordering Queue",
      screenName: "order_integrity_violation",
      jobId: input.jobId,
      frictionType: "Bug",
      severity: "High",
      status: "New",
      description:
        `[order_integrity_violation] ${input.description}\n` +
        `Expected BOM: ${input.expectedHash}\n` +
        `Actual BOM:   ${input.actualHash}\n` +
        `Drift detected at Push without a Signed Change Order on file. ` +
        `This indicates the BOM mutated outside the Change Order workflow. ` +
        `Investigate JobSpec binding, materialsList writes, and any direct edits.`,
      createdAt: new Date().toISOString(),
    });
    // Also void the offending draft so the user is forced through a fresh prep.
    voidDraft(input.jobId, "integrity_violation");
  }, [addEntry, role, roleInfo, voidDraft]);

  // ── Auto-void on Change Order signing ──────────────────────────────────
  // When a NEW Signed CO appears for a job that currently has a "prepared"
  // draft, void the draft with reason "change_order" and link the CO id.
  useEffect(() => {
    setDrafts((prev) => {
      let next = prev;
      for (const job of jobs) {
        const lastSigned = getLastSignedChangeOrderAt(job);
        const previousLastSigned = lastSignedCoRef.current[job.id];
        lastSignedCoRef.current[job.id] = lastSigned;

        const draft = next[job.id];
        if (!draft || draft.status !== "prepared") continue;

        // Trigger only when a *new* CO is signed after this draft was prepared.
        if (
          lastSigned &&
          lastSigned > draft.preparedAt &&
          lastSigned !== draft.lastSignedChangeOrderAtPrep &&
          lastSigned !== previousLastSigned
        ) {
          // Find the CO id for the backlink.
          const co = (job.changeOrders || []).find(
            (c) => c.status === "Signed" && c.signedAt === lastSigned,
          );
          next = {
            ...next,
            [job.id]: {
              ...draft,
              status: "voided",
              voidedAt: new Date().toISOString(),
              voidedReason: "change_order",
              voidedChangeOrderId: co?.id,
            },
          };
        }
      }
      return next;
    });
  }, [jobs]);

  const value = useMemo<OrderDraftContextValue>(() => ({
    drafts,
    getDraft,
    prepareDraft,
    voidDraft,
    markPushed,
    logIntegrityViolation,
  }), [drafts, getDraft, prepareDraft, voidDraft, markPushed, logIntegrityViolation]);

  return (
    <OrderDraftContext.Provider value={value}>{children}</OrderDraftContext.Provider>
  );
}

export function useOrderDraft() {
  const ctx = useContext(OrderDraftContext);
  if (!ctx) throw new Error("useOrderDraft must be used within OrderDraftProvider");
  return ctx;
}
